import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { ClickTarget, click } from '@tracking';
import { VeriffDecisionResponse, fetchVeriffDecision } from '@utils/backend-api/veriff';
import { MESSAGES, createVeriffFrame } from '@veriff/incontext-sdk';

import useCreateVeriffSession from './useCreateVeriffSession';
import useEventTracking from './useEventTracking';

interface UseVeriffSessionResponse {
  startVeriffSession: (exisitingName?: string, exisitingId?: number) => Promise<void>;
  isVeriffLoading: boolean;
}

const useVeriffSession = (): UseVeriffSessionResponse => {
  const [isVeriffLoading, setIsVeriffLoading] = useState(false);
  const [veriffSessionId, setVeriffSessionId] = useState(null);
  const { mutateAsync: createVeriffSession } = useCreateVeriffSession();
  const { trackEvent } = useEventTracking();
  const {
    user: { id, name },
    setUser,
  } = useSubscribeForm();

  const startVeriffSession = async (exisitingName?: string, exisitingId?: number) => {
    const fullName = exisitingName || name;
    const identifier = exisitingId || id;
    toast.success("We need to verify that you're 30 or under to continue.");
    const [firstName, lastName] = fullName ? fullName.split(/\s+/) : ['', ''];
    const { sessionId, sessionUrl } = await createVeriffSession({
      first_name: firstName,
      last_name: lastName,
      user_id: identifier.toString(),
    });

    if (!sessionId || !sessionUrl) {
      toast.success('Something went wrong. Please refresh the page or try again later.');
      return;
    }

    trackEvent(click({ clickTarget: ClickTarget.VeriffOpen }));
    createVeriffFrame({
      url: sessionUrl,
      onEvent: function (msg) {
        switch (msg) {
          case MESSAGES.FINISHED:
            setIsVeriffLoading(true);
            setVeriffSessionId(sessionId);
            trackEvent(click({ clickTarget: ClickTarget.VeriffFinish }));
            break;
          case MESSAGES.CANCELED:
            trackEvent(click({ clickTarget: ClickTarget.VeriffCancel }));
            break;
          case MESSAGES.STARTED:
            trackEvent(click({ clickTarget: ClickTarget.VeriffStart }));
            break;
          case MESSAGES.RELOAD_REQUEST:
            trackEvent(click({ clickTarget: ClickTarget.VeriffReload }));
            break;
          default:
            break;
        }
      },
    });
  };

  useEffect(() => {
    if (veriffSessionId) {
      toast.success('Please hold while we finish verifying your age.');
      let attempts = 0;
      const maxAttempts = 7;
      const intervalDuration = 5000;

      const fetchData = async () => {
        try {
          const { dobVerified, isYPEligible, message, success }: VeriffDecisionResponse =
            await fetchVeriffDecision({
              session_id: veriffSessionId,
              user_id: id,
            });

          if (success) {
            setUser((prevUser) => ({ ...prevUser, dobVerified, isYPEligible }));
            clearInterval(intervalId);
            setIsVeriffLoading(false);
            setVeriffSessionId(null);
            if (message) {
              toast.success(message);
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(intervalId);
            setIsVeriffLoading(false);
            toast.success(
              'Verification is taking longer than expected. Please come back in a few minutes.'
            );
          }
        } catch (error) {
          clearInterval(intervalId);
          setIsVeriffLoading(false);
          setVeriffSessionId(null);
          toast.error(error.message);
        }
      };

      const intervalId = setInterval(() => {
        attempts++;
        fetchData();
      }, intervalDuration);

      return () => clearInterval(intervalId);
    }
  }, [veriffSessionId, setUser, id]);

  return { startVeriffSession, isVeriffLoading };
};

export default useVeriffSession;
